import { Component } from '@theme/component';

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/**
 * A parsed numeric value plus a renderer that reproduces its original formatting.
 *
 * @typedef {Object} ParsedValue
 * @property {number} value - The numeric target.
 * @property {(n: number) => string} render - Formats a number back to the original style.
 */

/**
 * `<count-up>` — animates its numeric text from 0 up to the rendered value the
 * first time it scrolls into view. Reusable across the theme.
 *
 * The final value is server-rendered as the element's text, so with JS off (or
 * reduced motion on) the correct number is always shown — the animation only
 * ever replaces a value that's already there. Formatting (decimals, decimal and
 * thousands separators, any prefix/suffix like "%") is detected from that text
 * and reproduced on every frame, so it stays locale-correct.
 *
 * @property {string} [data-duration] - Animation length in ms (default 1500).
 * @property {string} [data-count-delay] - Delay in ms after the element scrolls
 *   into view before the count starts, held at zero meanwhile (default 0).
 *
 * @extends {Component}
 */
class CountUp extends Component {
  /** @type {number} */
  #raf = 0;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {ParsedValue | null} */
  #parsed = null;

  connectedCallback() {
    super.connectedCallback();

    this.#parsed = parseValue(this.textContent ?? '');

    if (!this.#parsed || reducedMotion.matches || !('IntersectionObserver' in window)) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#animate();
      },
      { threshold: 0.4 }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#raf);
  }

  #animate() {
    if (!this.#parsed) return;
    const { value, render } = this.#parsed;
    const duration = Number(this.dataset.duration) || 1500;
    const delay = Number(this.dataset.countDelay) || 0;
    const node = this.firstChild instanceof Text ? this.firstChild : this;
    const set = (n) => {
      const text = render(n);
      if (node instanceof Text) node.nodeValue = text;
      else this.textContent = text;
    };

    set(0);
    const startTime = performance.now() + delay;
    const tick = (now) => {
      const elapsed = now - startTime;
      if (elapsed <= 0) {
        this.#raf = requestAnimationFrame(tick);
        return;
      }
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); 
      set(value * eased);
      if (t < 1) {
        this.#raf = requestAnimationFrame(tick);
      } else {
        set(value); 
      }
    };
    this.#raf = requestAnimationFrame(tick);
  }
}

/**
 * Parses a formatted number string into its value and a matching renderer.
 *
 * @param {string} raw - e.g. "18,6 %", "43 %", "106.000".
 * @returns {ParsedValue | null}
 */
function parseValue(raw) {
  const text = raw.trim();
  const match = text.match(/-?\d[\d.,]*/);
  if (!match) return null;

  const numStr = match[0];
  const index = match.index ?? 0;
  const prefix = text.slice(0, index);
  const suffix = text.slice(index + numStr.length);

  const lastComma = numStr.lastIndexOf(',');
  const lastDot = numStr.lastIndexOf('.');
  let decimalSep = '';
  let groupSep = '';

  if (lastComma > -1 && lastDot > -1) {
    decimalSep = lastComma > lastDot ? ',' : '.';
    groupSep = decimalSep === ',' ? '.' : ',';
  } else if (lastComma > -1) {
    if (/^\d{1,3}(,\d{3})+$/.test(numStr)) groupSep = ',';
    else decimalSep = ',';
  } else if (lastDot > -1) {
    if (/^\d{1,3}(\.\d{3})+$/.test(numStr)) groupSep = '.';
    else decimalSep = '.';
  }

  const decimals = decimalSep ? numStr.length - numStr.lastIndexOf(decimalSep) - 1 : 0;

  let clean = numStr;
  if (groupSep) clean = clean.split(groupSep).join('');
  if (decimalSep) clean = clean.replace(decimalSep, '.');
  const value = Number.parseFloat(clean);
  if (Number.isNaN(value)) return null;

  const render = (n) => {
    const [int, frac] = n.toFixed(decimals).split('.');
    let grouped = int;
    if (groupSep) grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep);
    return prefix + grouped + (decimals > 0 ? decimalSep + frac : '') + suffix;
  };

  return { value, render };
}

if (!customElements.get('count-up')) {
  customElements.define('count-up', CountUp);
}
